import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, FileText, Gauge, Plus, Trash2 } from '@/components/icons';
import { api, errorMessage } from '@/lib/api';
import { DataFilesCard } from './DataFiles';
import { TestFilesCard } from './TestFiles';
import { SCRIPT_SOURCE } from '@/lib/perf';
import { useAuth } from '@/lib/auth';
import { timeAgo } from '@/lib/format';
import type { Project } from '@/lib/types';
import { Badge, Button, Card, EmptyState, ErrorNotice, LinkButton, PageLoader, useConfirm, useToast } from '@/components/ui';

/**
 * The project's performance script library: the journeys performance tests use in their scenarios. A script is made
 * once (recorded with the 1QA Recorder or uploaded as a HAR file) and used by any number of tests.
 */
export function ScriptsTab({ project }: { project: Project }) {
  const { can, canModify } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const scripts = useQuery({ queryKey: ['perf-scripts', project.id], queryFn: () => api.perfScripts(project.id) });
  const canWrite = can('test.write', project.orgId);
  const refresh = () => qc.invalidateQueries({ queryKey: ['perf-scripts', project.id] });
  const copy = useMutation({ mutationFn: (id: string) => api.copyPerfScript(id), onSuccess: () => (toast.success('Copied'), refresh()), onError: (e) => toast.error(errorMessage(e)) });
  const remove = useMutation({ mutationFn: (id: string) => api.deletePerfScript(id), onSuccess: () => (toast.success('Script deleted'), refresh()), onError: (e) => toast.error(errorMessage(e)) });

  if (scripts.isPending) return <PageLoader />;
  if (scripts.isError) return <ErrorNotice error={scripts.error} onRetry={() => scripts.refetch()} />;
  const actions = canWrite && (
    <div className="flex flex-wrap gap-2">
      <LinkButton to={`/projects/${project.id}/perf/scripts/new`} state={{ record: true }} variant="outline" size="sm" icon={<Plus className="size-4" />}>
        New script
      </LinkButton>
      {scripts.data.length > 0 && (
        <LinkButton to={`/projects/${project.id}/perf/new`} size="sm" icon={<Gauge className="size-4" />}>
          New performance test
        </LinkButton>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="min-w-0 flex-1 text-sm text-fg-secondary">Scripts are user journeys for performance tests. A performance test runs one or more of them, each with its share of the load.</p>
        {actions}
      </div>
      {scripts.data.length === 0 ? (
        <EmptyState
          icon={<FileText className="size-6" />}
          title="No scripts yet"
          description="Press New script to record a journey with the 1QA Recorder, or upload a HAR file from the browser’s DevTools or a proxy such as Charles or Fiddler."
        />
      ) : (
        <Card>
          <ul className="divide-y divide-border-subtle" data-testid="perf-scripts">
            {scripts.data.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary-subtle text-primary">
                  <FileText className="size-4" />
                </span>
                <Link to={`/projects/${project.id}/perf/scripts/${s.id}`} className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-fg hover:text-primary">{s.name}</div>
                  <div className="truncate text-xs text-fg-muted">
                    {SCRIPT_SOURCE[s.source]} · {s.transactions} transaction{s.transactions === 1 ? '' : 's'}, {s.requests} request{s.requests === 1 ? '' : 's'}
                    {s.websockets ? ` (${s.websockets} WebSocket)` : ''} · updated {timeAgo(s.updatedAt)}
                    {s.createdBy ? ` by ${s.createdBy.name}` : ''}
                  </div>
                </Link>
                {s.usedBy.length > 0 ? <Badge tone="neutral">Used by {s.usedBy.length === 1 ? s.usedBy[0]!.name : `${s.usedBy.length} tests`}</Badge> : <Badge tone="muted">Not used yet</Badge>}
                {canWrite && <Button variant="ghost" size="sm" aria-label={`Copy ${s.name}`} onClick={() => copy.mutate(s.id)} icon={<Copy className="size-4" />} />}
                {canModify('test.write', s.createdById, project.orgId) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Delete ${s.name}`}
                    disabled={s.usedBy.length > 0}
                    title={s.usedBy.length ? 'Used by a performance test' : undefined}
                    onClick={async () => {
                      if (await confirm({ title: 'Delete this script?', message: `“${s.name}” will be deleted.`, confirmLabel: 'Delete' })) remove.mutate(s.id);
                    }}
                    icon={<Trash2 className="size-4" />}
                  />
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
      <DataFilesCard projectId={project.id} orgId={project.orgId} />
      <TestFilesCard projectId={project.id} orgId={project.orgId} />
    </div>
  );
}
